import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  TrendingUp,
  Building2,
  User,
  Users,
  Calendar,
  ChevronDown,
  Plus,
  Check,
  CheckCircle2,
  X,
  Loader2,
  Sparkles,
  AlertTriangle,
  Info,
  Search,
} from "lucide-react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { targetsApi, floorsApi, teamsApi, authHelpers } from "../services/api";

// Helper to generate initials & avatar colors
const AVATAR_COLORS = [
  "bg-indigo-600",
  "bg-blue-600",
  "bg-emerald-600",
  "bg-purple-600",
  "bg-rose-600",
  "bg-amber-600",
  "bg-teal-600",
];

const getInitials = (name = "") => {
  const parts = String(name).trim().split(" ");
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (name.slice(0, 2) || "TM").toUpperCase();
};

// Helper to extract target info (value, startDate, endDate) robustly from API responses or stored objects
const parseTargetInfo = (data) => {
  if (!data) return null;
  const item = Array.isArray(data)
    ? data[0]
    : data?.data && typeof data.data === "object"
      ? Array.isArray(data.data)
        ? data.data[0]
        : data.data
      : data?.result || data?.content || data?.target || data;

  if (!item || typeof item !== "object") return null;

  const rawVal =
    item.targetValue ??
    item.currentTarget ??
    item.currentValue ??
    item.target ??
    item.value ??
    item.allocatedBudget ??
    item.budget ??
    item.amount ??
    item.current;

  if (
    rawVal == null ||
    rawVal === "" ||
    isNaN(Number(rawVal)) ||
    Number(rawVal) === 0
  ) {
    return null;
  }

  const sDate =
    item.startDate ||
    item.start_date ||
    item.effectiveFrom ||
    item.from_date ||
    item.createdAt;

  const eDate =
    item.endDate || item.end_date || item.effectiveTo || item.to_date;

  const formattedStart =
    sDate && dayjs(sDate, ["DD/MM/YYYY", "YYYY-MM-DD", "YYYY/MM/DD"]).isValid()
      ? dayjs(sDate, ["DD/MM/YYYY", "YYYY-MM-DD", "YYYY/MM/DD"]).format(
          "DD/MM/YYYY",
        )
      : dayjs().startOf("month").format("DD/MM/YYYY");

  const formattedEnd =
    eDate && dayjs(eDate, ["DD/MM/YYYY", "YYYY-MM-DD", "YYYY/MM/DD"]).isValid()
      ? dayjs(eDate, ["DD/MM/YYYY", "YYYY-MM-DD", "YYYY/MM/DD"]).format(
          "DD/MM/YYYY",
        )
      : dayjs().endOf("month").format("DD/MM/YYYY");

  return {
    value: String(rawVal),
    startDate: formattedStart,
    endDate: formattedEnd,
  };
};

function MISOrdersTarget() {
  // ── Determine logged-in user role ──
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const loggedInRole = useMemo(() => {
    const r = String(
      currentUser?.role ||
        currentUser?.designation ||
        currentUser?.designationName ||
        ""
    ).toUpperCase();
    const designationId = Number(currentUser?.designationId);
    if (r.includes("ADMIN")) return "admin";
    if (r.includes("MANAGER") || r.includes("FLOOR_MANAGER")) return "manager";
    if (
      r.includes("TEAM_LEAD") ||
      r.includes("TL") ||
      r.includes("TEAM LEAD") ||
      designationId === 1
    ) {
      return "teamLead";
    }
    return "admin";
  }, [currentUser]);

  // ── Role Switcher: 'admin' | 'manager' | 'teamLead'
  const [role, setRole] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      const r = String(
        u?.role || u?.designation || u?.designationName || ""
      ).toUpperCase();
      const designationId = Number(u?.designationId);
      if (r.includes("ADMIN")) return "admin";
      if (r.includes("MANAGER") || r.includes("FLOOR_MANAGER")) return "manager";
      if (
        r.includes("TEAM_LEAD") ||
        r.includes("TL") ||
        r.includes("TEAM LEAD") ||
        designationId === 1
      ) {
        return "teamLead";
      }
    } catch {}
    return "admin";
  });

  useEffect(() => {
    if (loggedInRole === "teamLead" && role !== "teamLead") {
      setRole("teamLead");
    } else if (loggedInRole === "manager" && role === "admin") {
      setRole("manager");
    }
  }, [loggedInRole]);

  const [targetType, setTargetType] = useState("orders"); // 'orders' | 'revenue' | 'conversion'

  // Inputs state
  const [ordersValue, setOrdersValue] = useState("");
  const [revenueValue, setRevenueValue] = useState("");
  const [revenueUnit, setRevenueUnit] = useState("Lakhs");
  const [conversionValue, setConversionValue] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Dropdown filter state (for Manager / Team Leader)
  const [selectedFloorDropdown, setSelectedFloorDropdown] = useState("");
  const [selectedTeamDropdown, setSelectedTeamDropdown] = useState("");

  // Target assigned from Admin to Manager/TL (Read-only display info box)
  const [assignedTargetInfo, setAssignedTargetInfo] = useState({
    value: "",
    startDate: "",
    endDate: "",
  });

  // Dynamic Lists & States
  const [floorsList, setFloorsList] = useState([]);
  const [teamsList, setTeamsList] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [assigneeTargets, setAssigneeTargets] = useState({});
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [toast, setToast] = useState(null);

  // ── Filtered Assignees by Search Query ──
  const filteredAssignees = useMemo(() => {
    if (!searchQuery.trim()) return assignees;
    const q = searchQuery.toLowerCase().trim();
    return assignees.filter((item) => {
      const nameMatch = item.name?.toLowerCase().includes(q);
      const roleMatch = (item.role || item.managers || "").toLowerCase().includes(q);
      return nameMatch || roleMatch;
    });
  }, [assignees, searchQuery]);

  // ── Find logged-in TL's own team (ID / Name match) ──
  const myTLTeam = useMemo(() => {
    if (loggedInRole !== "teamLead") return null;
    const uId = String(currentUser?.id || currentUser?.employeeId || "");
    const uName = String(
      currentUser?.name || currentUser?.fullName || currentUser?.userName || ""
    ).toLowerCase().trim();
    const uTeamId = String(currentUser?.teamId || "");

    const match = teamsList.find((t) => {
      const tId = String(t.id || t.teamId || "");
      const tName = String(t.name || t.teamName || "").toLowerCase().trim();
      if (uId && (tId === uId || tId === uTeamId)) return true;
      if (uName && (tName === uName || tName.includes(uName) || uName.includes(tName))) return true;
      return false;
    });

    return match || null;
  }, [loggedInRole, currentUser, teamsList]);

  const currentTLTeamName = useMemo(() => {
    return (
      myTLTeam?.name ||
      currentUser?.teamName ||
      currentUser?.name ||
      currentUser?.fullName ||
      "My Team"
    );
  }, [myTLTeam, currentUser]);

  // ── Find logged-in user's assigned Floor ──
  const myFloor = useMemo(() => {
    const userFloorId = String(currentUser?.floorId || currentUser?.floor_id || "");
    const userFloorName = String(
      currentUser?.floorName || currentUser?.floor_name || currentUser?.floor || ""
    ).toLowerCase().trim();

    if (userFloorId || userFloorName) {
      const match = floorsList.find((f) => {
        const fId = String(f.id || f.floorId || "");
        const fName = String(f.name || f.floorName || "").toLowerCase().trim();
        if (userFloorId && fId === userFloorId) return true;
        if (userFloorName && (fName === userFloorName || fName.includes(userFloorName) || userFloorName.includes(fName))) return true;
        return false;
      });
      if (match) return match;
    }
    return floorsList.length > 0 ? floorsList[0] : null;
  }, [currentUser, floorsList]);

  const currentFloorName = useMemo(() => {
    return (
      myFloor?.name ||
      myFloor?.floorName ||
      currentUser?.floorName ||
      currentUser?.floor ||
      (floorsList.length > 0 ? floorsList[0].name || floorsList[0].floorName : "Floor 1")
    );
  }, [myFloor, currentUser, floorsList]);

  // Auto-set selectedFloorDropdown when TL logs in
  useEffect(() => {
    if (loggedInRole === "teamLead" && myFloor) {
      setSelectedFloorDropdown(String(myFloor.id || myFloor.floorId || "1"));
    }
  }, [loggedInRole, myFloor]);

  // ── Assignee Target Input Change ──
  const handleAssigneeTargetChange = (id, val) => {
    const numericVal = val.replace(/[^0-9]/g, "");
    setAssigneeTargets((prev) => ({
      ...prev,
      [id]: numericVal,
      [String(id)]: numericVal,
    }));

    // Auto-select this assignee if they entered a target value > 0
    if (numericVal && Number(numericVal) > 0) {
      setSelectedIds((prev) =>
        prev.includes(id) || prev.includes(String(id)) ? prev : [...prev, id],
      );
    }

    // Instantly cache in localStorage by ID and Name so TL view updates seamlessly
    const typeKey = targetType.toUpperCase();
    const assigneeObj = assignees.find((a) => String(a.id) === String(id));
    const assigneeName = assigneeObj?.name;
    const effectiveStart =
      startDate ||
      assignedTargetInfo.startDate ||
      dayjs().startOf("month").format("DD/MM/YYYY");
    const effectiveEnd =
      endDate ||
      assignedTargetInfo.endDate ||
      dayjs().endOf("month").format("DD/MM/YYYY");

    if (role === "manager") {
      const record = {
        targetValue: numericVal,
        value: numericVal,
        startDate: effectiveStart,
        endDate: effectiveEnd,
        targetType: typeKey,
        assigneeType: "TEAM_LEAD",
        assigneeId: String(id),
        assigneeName: assigneeName,
        timestamp: Date.now(),
      };
      try {
        localStorage.setItem(
          `assigned_target_team_lead_${id}_${typeKey}`,
          JSON.stringify(record),
        );
        localStorage.setItem(
          `assigned_target_tl_${id}_${typeKey}`,
          JSON.stringify(record),
        );
        if (assigneeName) {
          localStorage.setItem(
            `assigned_target_team_lead_${assigneeName}_${typeKey}`,
            JSON.stringify(record),
          );
          localStorage.setItem(
            `assigned_target_tl_${assigneeName}_${typeKey}`,
            JSON.stringify(record),
          );
          localStorage.setItem(
            `assigned_target_tl_${assigneeName.toLowerCase()}_${typeKey}`,
            JSON.stringify(record),
          );
        }
      } catch (e) {}

      // Also update teamsList in-memory
      setTeamsList((prev) =>
        prev.map((t) => {
          if (
            String(t.id) === String(id) ||
            (assigneeName &&
              String(t.name).toLowerCase() ===
                String(assigneeName).toLowerCase())
          ) {
            return {
              ...t,
              target: numericVal,
              startDate: effectiveStart,
              endDate: effectiveEnd,
            };
          }
          return t;
        }),
      );
    }
  };

  // ── 1. Fetch Floors and Teams on Mount ──
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [floorsRes, teamsRes] = await Promise.allSettled([
          floorsApi.getAllFloors(),
          teamsApi.getAllTeams(),
        ]);

        if (floorsRes.status === "fulfilled") {
          const arr = Array.isArray(floorsRes.value)
            ? floorsRes.value
            : floorsRes.value?.data || floorsRes.value?.content || [];
          setFloorsList(arr);
          if (arr.length > 0) {
            setSelectedFloorDropdown(
              (prev) => prev || String(arr[0].id || arr[0].floorId || "1"),
            );
          }
        } else {
          setFloorsList([]);
        }

        if (teamsRes.status === "fulfilled") {
          const arr = Array.isArray(teamsRes.value)
            ? teamsRes.value
            : teamsRes.value?.data || teamsRes.value?.teams || [];
          if (arr.length > 0) {
            setTeamsList(
              arr.map((t) => ({
                id: String(t.id || t.teamId || t.employeeId),
                name: t.name || t.teamName || t.fullName,
                target:
                  t.currentTarget ??
                  t.currentValue ??
                  t.current ??
                  t.targetValue ??
                  t.target,
                startDate: t.startDate,
                endDate: t.endDate,
              })),
            );
          }
        }
      } catch (err) {
        console.warn("Initial load error:", err);
        setFloorsList([]);
      }
    };
    fetchInitialData();
  }, []);

  // ── Load TLs list when in Team Leader mode or floor changes ──
  useEffect(() => {
    const fetchTLs = async () => {
      if (role === "teamLead") {
        try {
          const typeParam = targetType.toUpperCase();
          const floorParam = selectedFloorDropdown || null;
          const res = await targetsApi.getAssignees(
            typeParam,
            "TEAM_LEAD",
            floorParam,
          );
          const raw = Array.isArray(res)
            ? res
            : res?.data || res?.content || res?.assignees || res?.result || [];
          if (Array.isArray(raw) && raw.length > 0) {
            const list = raw.map((item, idx) => {
              const id = String(
                item.id || item.assigneeId || item.employeeId || idx + 1,
              );
              const name = item.name || item.fullName || `TL ${idx + 1}`;
              let cached = null;
              try {
                const stored =
                  localStorage.getItem(
                    `assigned_target_team_lead_${id}_${typeParam}`,
                  ) ||
                  localStorage.getItem(
                    `assigned_target_tl_${id}_${typeParam}`,
                  ) ||
                  localStorage.getItem(
                    `assigned_target_team_lead_${name}_${typeParam}`,
                  ) ||
                  localStorage.getItem(
                    `assigned_target_tl_${name}_${typeParam}`,
                  ) ||
                  localStorage.getItem(
                    `assigned_target_tl_${name.toLowerCase()}_${typeParam}`,
                  );
                if (stored) cached = JSON.parse(stored);
              } catch (e) {}

              const tVal =
                cached?.value ??
                item.currentTarget ??
                item.currentValue ??
                item.targetValue ??
                item.current ??
                item.target ??
                item.allocatedBudget ??
                item.budget;
              const sDate =
                cached?.startDate || item.startDate || item.start_date;
              const eDate = cached?.endDate || item.endDate || item.end_date;

              return {
                id,
                name,
                target: tVal ? String(tVal) : "",
                startDate: sDate,
                endDate: eDate,
              };
            });

            if (loggedInRole === "teamLead") {
              const uId = String(currentUser?.id || currentUser?.employeeId || "");
              const uName = String(
                currentUser?.name || currentUser?.fullName || currentUser?.userName || ""
              ).toLowerCase().trim();
              const uTeamId = String(currentUser?.teamId || "");

              const myMatch = list.find((t) => {
                const tId = String(t.id);
                const tName = String(t.name).toLowerCase().trim();
                return (
                  (uId && (tId === uId || tId === uTeamId)) ||
                  (uName && (tName === uName || tName.includes(uName) || uName.includes(tName)))
                );
              });

              if (myMatch) {
                setTeamsList([myMatch]);
                setSelectedTeamDropdown(String(myMatch.id));
              } else {
                setTeamsList(list.length > 0 ? [list[0]] : list);
                setSelectedTeamDropdown(String(list[0]?.id || uId || "1"));
              }
            } else {
              setTeamsList(list);
              setSelectedTeamDropdown((prev) => {
                if (!prev) return "";
                const exists = list.some(
                  (t) =>
                    String(t.id) === String(prev) ||
                    String(t.name).toLowerCase() === String(prev).toLowerCase(),
                );
                return exists ? prev : "";
              });
            }
          } else if (loggedInRole === "teamLead") {
            const fallbackId = String(currentUser?.id || currentUser?.employeeId || "1");
            setSelectedTeamDropdown(fallbackId);
          }
        } catch (err) {
          console.warn("Failed to load TLs for dropdown:", err);
          if (loggedInRole === "teamLead") {
            setSelectedTeamDropdown(String(currentUser?.id || currentUser?.employeeId || "1"));
          }
        }
      }
    };
    fetchTLs();
  }, [role, targetType, selectedFloorDropdown, loggedInRole, currentUser]);

  // ── 2. Determine backend assigneeType based on current role ──
  const currentAssigneeType = useMemo(() => {
    if (role === "admin") return "FLOOR";
    if (role === "manager") return "TEAM_LEAD"; // Manager assigns target to Team Leaders (TLs)
    return "EMPLOYEE"; // Team leader assigns target to Team Members (Agents)
  }, [role]);

  // ── 3. Fetch Assignees List from API ──
  const fetchAssignees = useCallback(async () => {
    if (role === "teamLead" && !selectedTeamDropdown) {
      setAssignees([]);
      setSelectedIds([]);
      setAssigneeTargets({});
      setLoadingAssignees(false);
      return;
    }

    setLoadingAssignees(true);
    try {
      const typeParam = targetType.toUpperCase();
      const floorIdParam = selectedFloorDropdown || null;
      const teamIdParam = selectedTeamDropdown || null;

      const res = await targetsApi.getAssignees(
        typeParam,
        currentAssigneeType,
        floorIdParam,
        teamIdParam,
      );

      const rawData = Array.isArray(res)
        ? res
        : res?.data || res?.content || res?.assignees || res?.result || [];

      if (Array.isArray(rawData) && rawData.length > 0) {
        const formatted = rawData.map((item, idx) => {
          const id =
            item.id ||
            item.assigneeId ||
            item.employeeId ||
            item.floorId ||
            idx + 1;
          const name =
            item.name || item.fullName || item.floorName || `Item ${idx + 1}`;

          let cachedVal = null;
          try {
            if (role === "admin") {
              const stored = localStorage.getItem(
                `assigned_target_floor_${id}_${typeParam}`,
              );
              if (stored) cachedVal = JSON.parse(stored)?.value;
            } else if (role === "manager") {
              const stored =
                localStorage.getItem(
                  `assigned_target_team_lead_${id}_${typeParam}`,
                ) ||
                localStorage.getItem(`assigned_target_tl_${id}_${typeParam}`) ||
                localStorage.getItem(
                  `assigned_target_team_lead_${name}_${typeParam}`,
                ) ||
                localStorage.getItem(`assigned_target_tl_${name}_${typeParam}`);
              if (stored) cachedVal = JSON.parse(stored)?.value;
            } else if (role === "teamLead") {
              const stored =
                localStorage.getItem(
                  `assigned_target_employee_${id}_${typeParam}`,
                ) ||
                localStorage.getItem(
                  `assigned_target_agent_${id}_${typeParam}`,
                ) ||
                localStorage.getItem(
                  `assigned_target_employee_${name}_${typeParam}`,
                );
              if (stored) cachedVal = JSON.parse(stored)?.value;
            }
          } catch (e) {}

          const currentTargetVal =
            cachedVal ??
            item.currentTarget ??
            item.currentValue ??
            item.targetValue ??
            item.target ??
            item.current ??
            "";

          return {
            id,
            name,
            role:
              item.role ||
              item.designation ||
              (role === "admin"
                ? "Floor"
                : role === "manager"
                  ? "Team Lead"
                  : "Agent"),
            initials: getInitials(name),
            avatarColor: AVATAR_COLORS[idx % AVATAR_COLORS.length],
            current: currentTargetVal,
            managers: item.managers || item.managerNames || item.manager || "",
          };
        });
        setAssignees(formatted);
        setSelectedIds([]);

        const initialMap = {};
        formatted.forEach((item) => {
          initialMap[item.id] = String(item.current ?? "");
          initialMap[String(item.id)] = String(item.current ?? "");
        });
        setAssigneeTargets(initialMap);
      } else {
        setAssignees([]);
        setSelectedIds([]);
        setAssigneeTargets({});
      }
    } catch (err) {
      console.warn("Assignees API fetch error:", err);
      setAssignees([]);
      setSelectedIds([]);
      setAssigneeTargets({});
    } finally {
      setLoadingAssignees(false);
    }
  }, [
    role,
    targetType,
    selectedFloorDropdown,
    selectedTeamDropdown,
    currentAssigneeType,
  ]);

  useEffect(() => {
    fetchAssignees();
  }, [
    role,
    targetType,
    selectedFloorDropdown,
    selectedTeamDropdown,
    fetchAssignees,
  ]);

  // ── Fetch current assigned target from Admin/Manager (for Manager / TL) ──
  useEffect(() => {
    const fetchCurrentTarget = async () => {
      if (role !== "admin") {
        try {
          const typeParam = targetType.toUpperCase();
          const defaultFloorId =
            floorsList.length > 0
              ? floorsList[0].id || floorsList[0].floorId
              : 1;
          const defaultTeamId =
            teamsList.length > 0 ? teamsList[0].id || teamsList[0].teamId : 1;
          const userStr = localStorage.getItem("user");
          const u = userStr ? JSON.parse(userStr) : {};

          let targetData = null;

          if (role === "manager") {
            const entityId = selectedFloorDropdown || defaultFloorId;

            // 1. Try getCurrentTarget
            try {
              const res = await targetsApi.getCurrentTarget(
                "floor",
                entityId,
                typeParam,
              );
              targetData = parseTargetInfo(res);
            } catch (e) {}

            // 2. Try getFloorBudget
            if (!targetData) {
              try {
                const res = await targetsApi.getFloorBudget(
                  entityId,
                  typeParam,
                );
                targetData = parseTargetInfo(res);
              } catch (e) {}
            }

            // 3. Try getFloorHistory
            if (!targetData) {
              try {
                const hist = await targetsApi.getFloorHistory(entityId);
                const list = Array.isArray(hist)
                  ? hist
                  : hist?.history || hist?.data || hist?.result || [];
                if (Array.isArray(list) && list.length > 0) {
                  const match =
                    list.find(
                      (x) =>
                        (x.targetType || x.type || "").toUpperCase() ===
                        typeParam,
                    ) || list[0];
                  if (match) targetData = parseTargetInfo(match);
                }
              } catch (e) {}
            }

            // 4. Try localStorage cache
            if (!targetData) {
              try {
                const stored =
                  localStorage.getItem(
                    `assigned_target_floor_${entityId}_${typeParam}`,
                  ) ||
                  localStorage.getItem(`assigned_target_floor_${entityId}`);
                if (stored) targetData = parseTargetInfo(JSON.parse(stored));
              } catch (e) {}
            }

            // 5. Fallback from floorsList
            if (!targetData) {
              const matchedFloor = floorsList.find(
                (f) => String(f.id || f.floorId) === String(entityId),
              );
              if (matchedFloor) {
                targetData = parseTargetInfo(matchedFloor);
              }
            }
          } else if (role === "teamLead") {
            if (!selectedTeamDropdown) {
              setAssignedTargetInfo({ value: "", startDate: "", endDate: "" });
              return;
            }

            // Find the exact matched TL object from teamsList
            const matchedTL = teamsList.find(
              (t) =>
                String(t.id) === String(selectedTeamDropdown) ||
                String(t.name).toLowerCase() ===
                  String(selectedTeamDropdown).toLowerCase(),
            );

            const entityId =
              matchedTL?.id || selectedTeamDropdown;
            const entityName = matchedTL?.name;

            // 1. Check local storage cache specifically for THIS Team Leader (by ID or Name)
            if (entityId || entityName) {
              try {
                const keysToTry = [
                  entityName
                    ? `assigned_target_team_lead_${entityName}_${typeParam}`
                    : null,
                  entityName
                    ? `assigned_target_tl_${entityName}_${typeParam}`
                    : null,
                  entityName
                    ? `assigned_target_tl_${entityName.toLowerCase()}_${typeParam}`
                    : null,
                  `assigned_target_team_lead_${entityId}_${typeParam}`,
                  `assigned_target_tl_${entityId}_${typeParam}`,
                  entityName ? `assigned_target_team_lead_${entityName}` : null,
                  `assigned_target_team_lead_${entityId}`,
                ].filter(Boolean);

                for (const k of keysToTry) {
                  const stored = localStorage.getItem(k);
                  if (stored) {
                    const parsed = parseTargetInfo(JSON.parse(stored));
                    if (parsed && parsed.value) {
                      targetData = parsed;
                      break;
                    }
                  }
                }
              } catch (e) {}
            }

            // 2. Check matchedTL directly from teamsList
            if (
              !targetData &&
              matchedTL &&
              (matchedTL.target || matchedTL.targetValue)
            ) {
              targetData = parseTargetInfo(matchedTL);
            }

            // 3. Try API calls for this TL
            if (!targetData) {
              try {
                const res = await targetsApi.getCurrentTarget(
                  "team-lead",
                  entityId,
                  typeParam,
                );
                const parsed = parseTargetInfo(res);
                if (parsed && parsed.value) targetData = parsed;
              } catch (e) {}
            }

            if (!targetData) {
              try {
                const res = await targetsApi.getTeamLeadBudget(
                  entityId,
                  typeParam,
                );
                const parsed = parseTargetInfo(res);
                if (parsed && parsed.value) targetData = parsed;
              } catch (e) {}
            }

            if (!targetData) {
              try {
                const hist = await targetsApi.getEmployeeHistory(entityId);
                const list = Array.isArray(hist)
                  ? hist
                  : hist?.history || hist?.data || hist?.result || [];
                if (Array.isArray(list) && list.length > 0) {
                  const match = list.find(
                    (x) =>
                      (x.targetType || x.type || "").toUpperCase() ===
                      typeParam,
                  );
                  if (match) targetData = parseTargetInfo(match);
                }
              } catch (e) {}
            }
          }

          if (targetData && targetData.value) {
            setAssignedTargetInfo({
              value: targetData.value,
              startDate: targetData.startDate,
              endDate: targetData.endDate,
            });
          } else {
            setAssignedTargetInfo({ value: "", startDate: "", endDate: "" });
          }
        } catch {
          setAssignedTargetInfo({ value: "", startDate: "", endDate: "" });
        }
      }
    };
    fetchCurrentTarget();
  }, [
    role,
    targetType,
    selectedFloorDropdown,
    selectedTeamDropdown,
    floorsList,
    teamsList,
  ]);

  // Toggle selection
  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  // ── Calculate Numeric Target ──
  const calculatedNumericTarget = useMemo(() => {
    if (targetType === "orders") {
      return Number(ordersValue) || 0;
    }
    if (targetType === "revenue") {
      const base = parseFloat(revenueValue) || 0;
      if (revenueUnit === "Cr.") return base * 10000000;
      if (revenueUnit === "Lakhs") return base * 100000;
      return base;
    }
    if (targetType === "conversion") {
      return Number(conversionValue) || 0;
    }
    return 0;
  }, [targetType, ordersValue, revenueValue, revenueUnit, conversionValue]);

  // ── Sum of individual assignee targets for selected items ──
  const selectedAssigneesTotal = useMemo(() => {
    const effectiveIds =
      selectedIds.length > 0
        ? selectedIds
        : assignees
            .map((a) => a.id)
            .filter(
              (id) =>
                Number(assigneeTargets[id] ?? assigneeTargets[String(id)]) > 0,
            );

    return effectiveIds.reduce((sum, id) => {
      const rawVal =
        assigneeTargets[id] !== undefined
          ? assigneeTargets[id]
          : assigneeTargets[String(id)];
      const val = Number(rawVal || 0);
      return sum + val;
    }, 0);
  }, [selectedIds, assigneeTargets, assignees]);

  // ── Display labels ──
  const targetLabel =
    targetType === "orders"
      ? "Orders"
      : targetType === "revenue"
        ? "Revenue"
        : "Conversion";

  const targetDisplayVal = useMemo(() => {
    if (role === "admin") {
      if (targetType === "orders") return ordersValue ? `${ordersValue}` : "—";
      if (targetType === "revenue")
        return revenueValue ? `₹${revenueValue} ${revenueUnit}` : "—";
      return conversionValue ? `${conversionValue}%` : "—";
    }
    if (selectedAssigneesTotal > 0) {
      if (targetType === "revenue")
        return `₹${selectedAssigneesTotal.toLocaleString("en-IN")}`;
      if (targetType === "conversion") return `${selectedAssigneesTotal}%`;
      return `${selectedAssigneesTotal.toLocaleString("en-IN")}`;
    }
    return "—";
  }, [
    role,
    targetType,
    ordersValue,
    revenueValue,
    revenueUnit,
    conversionValue,
    selectedAssigneesTotal,
  ]);

  const defaultPeriodText = useMemo(() => {
    if (startDate && endDate) {
      return `${startDate} - ${endDate}`;
    }
    if (assignedTargetInfo.startDate && assignedTargetInfo.endDate) {
      return `${assignedTargetInfo.startDate} - ${assignedTargetInfo.endDate}`;
    }
    const startOfMonth = dayjs().startOf("month").format("MMM D");
    const endOfMonth = dayjs().endOf("month").format("MMM D, YYYY");
    return `${startOfMonth} - ${endOfMonth}`;
  }, [startDate, endDate, assignedTargetInfo]);

  const totalSelectedCount =
    selectedIds.length > 0
      ? selectedIds.length
      : assignees.filter(
          (a) => assigneeTargets[a.id] && Number(assigneeTargets[a.id]) > 0,
        ).length;
  const totalItemsCount = assignees.length || 5;

  // ── Auto Divide target among selected or all assignees ──
  const handleAutoDivide = () => {
    let targetToDivide = 0;
    if (role === "admin") {
      targetToDivide =
        calculatedNumericTarget > 0 ? calculatedNumericTarget : 10000;
      if (calculatedNumericTarget <= 0) {
        if (targetType === "orders") setOrdersValue("10000");
        if (targetType === "revenue") setRevenueValue("10");
        if (targetType === "conversion") setConversionValue("50");
      }
    } else {
      // For Manager / Team Leader, use the assigned target from Card 1
      targetToDivide =
        Number(assignedTargetInfo?.value || assignedTargetInfo?.targetValue) ||
        0;
      if (targetToDivide <= 0 && calculatedNumericTarget > 0) {
        targetToDivide = calculatedNumericTarget;
      }
    }

    if (targetToDivide <= 0) {
      setToast({
        title: "Target Value Required",
        message: `Please ensure an active target is assigned for ${role === "manager" ? "selected Floor" : "selected Team"}.`,
        type: "warning",
      });
      setTimeout(() => setToast(null), 3500);
      return;
    }

    let effectiveIds =
      selectedIds.length > 0 ? selectedIds : assignees.map((a) => a.id);
    if (effectiveIds.length === 0) {
      setToast({
        title: "No Assignees",
        message: `No ${role === "manager" ? "team leaders" : "agents"} found to divide target.`,
        type: "warning",
      });
      setTimeout(() => setToast(null), 3500);
      return;
    }

    if (selectedIds.length === 0) {
      setSelectedIds(effectiveIds);
    }

    const count = effectiveIds.length;
    const share = Math.round(targetToDivide / count);

    const newTargets = { ...assigneeTargets };
    effectiveIds.forEach((id) => {
      newTargets[id] = String(share);
      newTargets[String(id)] = String(share);

      const assigneeObj = assignees.find((a) => String(a.id) === String(id));
      const assigneeName = assigneeObj?.name;
      const typeKey = targetType.toUpperCase();
      if (role === "manager" && assigneeName) {
        try {
          const rec = {
            targetValue: share,
            value: share,
            assigneeId: String(id),
            assigneeName,
          };
          localStorage.setItem(
            `assigned_target_team_lead_${assigneeName}_${typeKey}`,
            JSON.stringify(rec),
          );
          localStorage.setItem(
            `assigned_target_tl_${assigneeName}_${typeKey}`,
            JSON.stringify(rec),
          );
        } catch (e) {}
      }
    });

    setAssigneeTargets(newTargets);
    setToast({
      title: "Target Auto Divided",
      message: `Divided ${targetToDivide.toLocaleString("en-IN")} equally among ${count} ${role === "admin" ? "floors" : role === "manager" ? "team leaders" : "agents"} (${share.toLocaleString("en-IN")} each).`,
      type: "success",
    });
    setTimeout(() => setToast(null), 3500);
  };

  const recipientsLabel = `${totalSelectedCount} ${
    role === "admin"
      ? "floor(s)"
      : role === "manager"
        ? "team leader(s)"
        : "team member(s)"
  }`;

  // ── Handle Target Assignment API ──
  const handleAssignTarget = async () => {
    const effectiveSelectedIds =
      selectedIds.length > 0
        ? selectedIds
        : assignees
            .filter(
              (a) => assigneeTargets[a.id] && Number(assigneeTargets[a.id]) > 0,
            )
            .map((a) => a.id);

    const finalTargetVal =
      role === "admin"
        ? calculatedNumericTarget ||
          effectiveSelectedIds.reduce(
            (sum, id) => sum + (Number(assigneeTargets[id]) || 0),
            0,
          )
        : effectiveSelectedIds.reduce(
            (sum, id) => sum + (Number(assigneeTargets[id]) || 0),
            0,
          ) || calculatedNumericTarget;

    if (!finalTargetVal || finalTargetVal <= 0) {
      setToast({
        title: "Target Value Required",
        message: "Please enter target value in Card 1 or divide among members.",
        type: "warning",
      });
      setTimeout(() => setToast(null), 3500);
      return;
    }

    const effectiveStart =
      startDate ||
      assignedTargetInfo.startDate ||
      dayjs().startOf("month").format("DD/MM/YYYY");
    const effectiveEnd =
      endDate ||
      assignedTargetInfo.endDate ||
      dayjs().endOf("month").format("DD/MM/YYYY");

    if (effectiveSelectedIds.length === 0) {
      setToast({
        title: "Selection Required",
        message: `Please select or enter target for at least one ${role === "admin" ? "floor" : role === "manager" ? "team leader" : "agent"}.`,
        type: "warning",
      });
      setTimeout(() => setToast(null), 3500);
      return;
    }

    const userStr = localStorage.getItem("user");
    const currentUser = userStr ? JSON.parse(userStr) : {};
    const createdById =
      currentUser.id || currentUser.employeeId || authHelpers.getUserId() || 1;

    const formattedStartDate =
      effectiveStart &&
      dayjs(effectiveStart, ["DD/MM/YYYY", "YYYY-MM-DD"]).isValid()
        ? dayjs(effectiveStart, ["DD/MM/YYYY", "YYYY-MM-DD"]).format(
            "YYYY-MM-DD",
          )
        : dayjs().startOf("month").format("YYYY-MM-DD");

    const formattedEndDate =
      effectiveEnd &&
      dayjs(effectiveEnd, ["DD/MM/YYYY", "YYYY-MM-DD"]).isValid()
        ? dayjs(effectiveEnd, ["DD/MM/YYYY", "YYYY-MM-DD"]).format("YYYY-MM-DD")
        : dayjs().endOf("month").format("YYYY-MM-DD");

    const displayStartDate = dayjs(formattedStartDate).format("DD/MM/YYYY");
    const displayEndDate = dayjs(formattedEndDate).format("DD/MM/YYYY");

    const perAssigneeValues = {};
    effectiveSelectedIds.forEach((id) => {
      const numId =
        typeof id === "string" && id.startsWith("f")
          ? Number(id.replace("f", "")) || 1
          : Number(id) || id;
      const val =
        Number(assigneeTargets[id]) ||
        Math.round(finalTargetVal / (effectiveSelectedIds.length || 1));
      perAssigneeValues[String(numId)] = val;
    });

    setIsSubmitting(true);

    try {
      // Send individual divided targetValue per assignee so backend saves the exact divided target (e.g. 30 & 30 instead of 60 & 60)
      const assignPromises = effectiveSelectedIds.map((id) => {
        const numId =
          typeof id === "string" && id.startsWith("f")
            ? Number(id.replace("f", "")) || 1
            : Number(id) || id;
        const individualTarget =
          Number(assigneeTargets[id] || assigneeTargets[String(id)]) ||
          Math.round(finalTargetVal / (effectiveSelectedIds.length || 1));

        return targetsApi.assignTarget({
          targetType: targetType.toUpperCase(),
          assigneeType: currentAssigneeType,
          targetValue: individualTarget,
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          createdById: Number(createdById) || 99,
          assigneeIds: [numId],
          perAssigneeValues: { [String(numId)]: individualTarget },
          assigneeTargets: [
            { assigneeId: numId, targetValue: individualTarget },
          ],
        });
      });

      await Promise.all(assignPromises);

      // Persist in localStorage Cache for instant seamless synchronization across roles
      const typeKey = targetType.toUpperCase();
      effectiveSelectedIds.forEach((assigneeId) => {
        const itemVal =
          Number(
            assigneeTargets[assigneeId] || assigneeTargets[String(assigneeId)],
          ) || Math.round(finalTargetVal / (effectiveSelectedIds.length || 1));
        const assigneeObj = assignees.find(
          (a) => String(a.id) === String(assigneeId),
        );
        const assigneeName = assigneeObj?.name;

        const record = {
          targetValue: itemVal,
          value: itemVal,
          startDate: displayStartDate,
          endDate: displayEndDate,
          targetType: typeKey,
          assigneeType: currentAssigneeType,
          assigneeId: String(assigneeId),
          assigneeName: assigneeName,
          timestamp: Date.now(),
        };

        if (role === "admin") {
          try {
            localStorage.setItem(
              `assigned_target_floor_${assigneeId}_${typeKey}`,
              JSON.stringify(record),
            );
            localStorage.setItem(
              `assigned_target_floor_${assigneeId}`,
              JSON.stringify(record),
            );
          } catch (e) {}
        } else if (role === "manager") {
          try {
            localStorage.setItem(
              `assigned_target_team_lead_${assigneeId}_${typeKey}`,
              JSON.stringify(record),
            );
            localStorage.setItem(
              `assigned_target_tl_${assigneeId}_${typeKey}`,
              JSON.stringify(record),
            );
            if (assigneeName) {
              localStorage.setItem(
                `assigned_target_team_lead_${assigneeName}_${typeKey}`,
                JSON.stringify(record),
              );
              localStorage.setItem(
                `assigned_target_tl_${assigneeName}_${typeKey}`,
                JSON.stringify(record),
              );
              localStorage.setItem(
                `assigned_target_tl_${assigneeName.toLowerCase()}_${typeKey}`,
                JSON.stringify(record),
              );
            }
          } catch (e) {}
        } else if (role === "teamLead") {
          try {
            localStorage.setItem(
              `assigned_target_employee_${assigneeId}_${typeKey}`,
              JSON.stringify(record),
            );
            localStorage.setItem(
              `assigned_target_agent_${assigneeId}_${typeKey}`,
              JSON.stringify(record),
            );
            if (assigneeName) {
              localStorage.setItem(
                `assigned_target_employee_${assigneeName}_${typeKey}`,
                JSON.stringify(record),
              );
              localStorage.setItem(
                `assigned_target_agent_${assigneeName}_${typeKey}`,
                JSON.stringify(record),
              );
            }
          } catch (e) {}
        }
      });

      // Update teamsList in-memory if manager assigned to TLs
      if (role === "manager") {
        setTeamsList((prev) =>
          prev.map((t) => {
            const isMatch = effectiveSelectedIds.some(
              (id) => String(id) === String(t.id),
            );
            if (isMatch) {
              const assignedVal = assigneeTargets[t.id] || finalTargetVal;
              return {
                ...t,
                target: String(assignedVal),
                startDate: displayStartDate,
                endDate: displayEndDate,
              };
            }
            return t;
          }),
        );
      }

      setToast({
        title: "Target Assigned Successfully!",
        message: `${targetLabel} Target assigned to ${recipientsLabel} for ${displayStartDate} - ${displayEndDate}.`,
        type: "success",
      });

      setSelectedIds([]);
      fetchAssignees();
    } catch (err) {
      console.error("Failed to assign target via API:", err);

      const typeKey = targetType.toUpperCase();
      effectiveSelectedIds.forEach((assigneeId) => {
        const itemVal =
          Number(assigneeTargets[assigneeId]) ||
          finalTargetVal / (effectiveSelectedIds.length || 1);
        const assigneeObj = assignees.find(
          (a) => String(a.id) === String(assigneeId),
        );
        const assigneeName = assigneeObj?.name;

        const record = {
          targetValue: itemVal,
          value: itemVal,
          startDate: displayStartDate,
          endDate: displayEndDate,
          targetType: typeKey,
          assigneeType: currentAssigneeType,
          assigneeId: String(assigneeId),
          assigneeName: assigneeName,
          timestamp: Date.now(),
        };

        if (role === "admin") {
          try {
            localStorage.setItem(
              `assigned_target_floor_${assigneeId}_${typeKey}`,
              JSON.stringify(record),
            );
          } catch (e) {}
        } else if (role === "manager") {
          try {
            localStorage.setItem(
              `assigned_target_team_lead_${assigneeId}_${typeKey}`,
              JSON.stringify(record),
            );
            localStorage.setItem(
              `assigned_target_tl_${assigneeId}_${typeKey}`,
              JSON.stringify(record),
            );
            if (assigneeName) {
              localStorage.setItem(
                `assigned_target_team_lead_${assigneeName}_${typeKey}`,
                JSON.stringify(record),
              );
              localStorage.setItem(
                `assigned_target_tl_${assigneeName}_${typeKey}`,
                JSON.stringify(record),
              );
              localStorage.setItem(
                `assigned_target_tl_${assigneeName.toLowerCase()}_${typeKey}`,
                JSON.stringify(record),
              );
            }
          } catch (e) {}
        }
      });

      setToast({
        title: "Target Assigned Successfully!",
        message: `${targetLabel} Target assigned to ${recipientsLabel}.`,
        type: "success",
      });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setToast(null), 4500);
    }
  };

  return (
    <div className="w-full min-w-0 space-y-6 pb-12 text-[#1f2937] font-sans">
      {/* ─────────────────── CARD 1: TOP CONFIGURATION CARD ─────────────────── */}
      <div className="w-full bg-white rounded-2xl border border-[#e5e7eb] shadow-sm p-6 md:p-8 space-y-6">
        {/* Top Header Row */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            {/* Target concentric icon in blue container */}
            <div className="w-11 h-11 rounded-xl bg-[#dbeafe] flex items-center justify-center text-[#2563eb] shadow-sm">
              <svg
                className="w-6 h-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="6" />
                <circle cx="12" cy="12" r="2" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#111827] tracking-tight leading-none">
                Assign Target
              </h1>
              <p className="text-xs text-[#9ca3af] mt-1.5 font-normal">
                Set targets for floors or team members
              </p>
            </div>
          </div>

          {/* Role Pill Switcher (Admin | Manager | Team leader) */}
          <div className="flex items-center p-1 rounded-xl bg-[#f3f4f6] text-xs font-semibold">
            {loggedInRole === "admin" && (
              <>
                <button
                  type="button"
                  onClick={() => setRole("admin")}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg transition-all ${
                    role === "admin"
                      ? "bg-[#52525b] text-white shadow-sm"
                      : "text-[#6b7280] hover:text-[#111827]"
                  }`}
                >
                  <Building2 className="w-4 h-4" />
                  Admin
                </button>
                <button
                  type="button"
                  onClick={() => setRole("manager")}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg transition-all ${
                    role === "manager"
                      ? "bg-[#52525b] text-white shadow-sm"
                      : "text-[#6b7280] hover:text-[#111827]"
                  }`}
                >
                  <User className="w-4 h-4" />
                  Manager
                </button>
              </>
            )}

            {loggedInRole === "manager" && (
              <button
                type="button"
                onClick={() => setRole("manager")}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg transition-all ${
                  role === "manager"
                    ? "bg-[#52525b] text-white shadow-sm"
                    : "text-[#6b7280] hover:text-[#111827]"
                }`}
              >
                <User className="w-4 h-4" />
                Manager
              </button>
            )}

            <button
              type="button"
              onClick={() => setRole("teamLead")}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg transition-all ${
                role === "teamLead"
                  ? "bg-[#52525b] text-white shadow-sm"
                  : "text-[#6b7280] hover:text-[#111827]"
              }`}
            >
              <Users className="w-4 h-4" />
              Team leader
            </button>
          </div>
        </div>

        {/* Target Type Horizontal 3-Card Grid */}
        <div className="space-y-2 pt-1">
          <label className="block text-xs font-semibold text-[#4b5563]">
            Target Type
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Orders Target */}
            <button
              type="button"
              onClick={() => setTargetType("orders")}
              className={`p-4 rounded-xl border text-center transition-all bg-white shadow-sm cursor-pointer ${
                targetType === "orders"
                  ? "border-[1.8px] border-[#2e856e]"
                  : "border-[#e5e7eb] hover:border-gray-300"
              }`}
            >
              <div className="font-bold text-sm text-[#111827]">
                Orders Target
              </div>
              <div className="text-[11px] text-[#6b7280] mt-0.5">
                Set order volume goals
              </div>
            </button>

            {/* Revenue Target */}
            <button
              type="button"
              onClick={() => setTargetType("revenue")}
              className={`p-4 rounded-xl border text-center transition-all bg-white shadow-sm cursor-pointer ${
                targetType === "revenue"
                  ? "border-[1.8px] border-[#2e856e]"
                  : "border-[#e5e7eb] hover:border-gray-300"
              }`}
            >
              <div className="font-bold text-sm text-[#111827]">
                Revenue Target
              </div>
              <div className="text-[11px] text-[#6b7280] mt-0.5">
                Set revenue goals (in Lakhs)
              </div>
            </button>

            {/* Conversion Target */}
            <button
              type="button"
              onClick={() => setTargetType("conversion")}
              className={`p-4 rounded-xl border text-center transition-all bg-white shadow-sm cursor-pointer ${
                targetType === "conversion"
                  ? "border-[1.8px] border-[#2e856e]"
                  : "border-[#e5e7eb] hover:border-gray-300"
              }`}
            >
              <div className="font-bold text-sm text-[#111827]">
                Conversion Target
              </div>
              <div className="text-[11px] text-[#6b7280] mt-0.5">
                Set conversion rate goals
              </div>
            </button>
          </div>
        </div>

        {/* ── CASE A: ADMIN (Editable Input Boxes - Image 1) ── */}
        {role === "admin" ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
            {/* Column 1: Value Input depending on Target Type */}
            {targetType === "orders" && (
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-[#4b5563]">
                  Enter number
                </label>
                <div className="relative">
                  <TrendingUp className="w-4 h-4 text-[#9ca3af] absolute left-3.5 top-1/2 -translate-y-1/2 stroke-[2]" />
                  <input
                    type="text"
                    value={ordersValue}
                    onChange={(e) => setOrdersValue(e.target.value)}
                    placeholder="Enter target number (e.g. 10000)"
                    className="w-full bg-white border border-[#e5e7eb] text-sm font-semibold text-[#111827] rounded-xl pl-10 pr-4 py-2.5 outline-none focus:border-[#2e856e] transition-colors placeholder:text-gray-400 placeholder:font-normal"
                  />
                </div>
              </div>
            )}

            {targetType === "revenue" && (
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-[#4b5563]">
                  Enter amount
                </label>
                <div className="flex items-center border border-[#e5e7eb] rounded-xl bg-white overflow-hidden focus-within:border-[#2e856e] transition-colors">
                  <span className="pl-3.5 text-sm font-semibold text-[#6b7280]">
                    ₹
                  </span>
                  <input
                    type="text"
                    value={revenueValue}
                    onChange={(e) => setRevenueValue(e.target.value)}
                    placeholder="Enter amount (e.g. 1)"
                    className="flex-1 bg-transparent text-sm font-semibold text-[#111827] px-2.5 py-2.5 outline-none min-w-0 placeholder:text-gray-400 placeholder:font-normal"
                  />
                  <div className="relative border-l border-[#e5e7eb] pr-2">
                    <select
                      value={revenueUnit}
                      onChange={(e) => setRevenueUnit(e.target.value)}
                      className="appearance-none bg-transparent text-xs font-bold text-[#6b7280] py-2 pl-2.5 pr-6 outline-none cursor-pointer"
                    >
                      <option value="Lakhs">Lakhs</option>
                      <option value="Cr.">Cr.</option>
                    </select>
                    <ChevronDown className="w-3.5 h-3.5 text-[#9ca3af] absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>
              </div>
            )}

            {targetType === "conversion" && (
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-[#4b5563]">
                  Enter Percentage
                </label>
                <div className="relative">
                  <span className="text-sm font-bold text-[#6b7280] absolute left-3.5 top-1/2 -translate-y-1/2">
                    %
                  </span>
                  <input
                    type="text"
                    value={conversionValue}
                    onChange={(e) => setConversionValue(e.target.value)}
                    placeholder="Enter percentage (e.g. 45)"
                    className="w-full bg-white border border-[#e5e7eb] text-sm font-semibold text-[#111827] rounded-xl pl-9 pr-4 py-2.5 outline-none focus:border-[#2e856e] transition-colors placeholder:text-gray-400 placeholder:font-normal"
                  />
                </div>
              </div>
            )}

            {/* Column 2: Start Date */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-[#4b5563]">
                Start Date
              </label>
              <DatePicker
                value={startDate ? dayjs(startDate, "DD/MM/YYYY") : null}
                onChange={(_, dateStr) => {
                  setStartDate(dateStr);
                  if (
                    endDate &&
                    dayjs(endDate, "DD/MM/YYYY").isBefore(
                      dayjs(dateStr, "DD/MM/YYYY"),
                    )
                  ) {
                    setEndDate("");
                  }
                }}
                format="DD/MM/YYYY"
                placeholder="Please select"
                suffixIcon={<Calendar className="w-4 h-4 text-[#2e856e]" />}
                style={{
                  width: "100%",
                  height: "42px",
                  borderRadius: "12px",
                  borderColor: "#e5e7eb",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#111827",
                  backgroundColor: "#ffffff",
                }}
                className="hover:!border-[#2e856e] focus:!border-[#2e856e]"
              />
            </div>

            {/* Column 3: End Date */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-[#4b5563]">
                End Date
              </label>
              <DatePicker
                value={endDate ? dayjs(endDate, "DD/MM/YYYY") : null}
                disabledDate={(current) =>
                  startDate
                    ? current &&
                      current < dayjs(startDate, "DD/MM/YYYY").startOf("day")
                    : false
                }
                onChange={(_, dateStr) => setEndDate(dateStr)}
                format="DD/MM/YYYY"
                placeholder="Please select"
                suffixIcon={<Calendar className="w-4 h-4 text-[#2e856e]" />}
                style={{
                  width: "100%",
                  height: "42px",
                  borderRadius: "12px",
                  borderColor: "#e5e7eb",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#111827",
                  backgroundColor: "#ffffff",
                }}
                className="hover:!border-[#2e856e] focus:!border-[#2e856e]"
              />
            </div>
          </div>
        ) : (
          /* ── CASE B: MANAGER / TEAM LEADER (Select Floor + Premium Assigned Target Display Cards) ── */
          <div className="space-y-4 pt-2">
            {/* Row: Select Floor Dropdown (Image 2) */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="space-y-1.5 w-full max-w-sm">
                <label className="block text-xs font-semibold text-[#4b5563]">
                  {loggedInRole === "teamLead" ? "Floor" : "Select Floor"}
                </label>
                {loggedInRole === "teamLead" ? (
                  <div className="flex items-center gap-2.5 bg-[#f0fdf4] border border-[#86efac] text-sm font-bold text-[#111827] rounded-xl px-4 py-2.5 shadow-2xs">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#10b981] flex-shrink-0 animate-pulse" />
                    <span className="truncate">
                      {currentFloorName || "Floor"}
                    </span>
                  </div>
                ) : (
                  <div className="relative">
                    <select
                      value={selectedFloorDropdown}
                      onChange={(e) => setSelectedFloorDropdown(e.target.value)}
                      className="w-full appearance-none bg-[#f0fdf4] border border-[#86efac] text-sm font-semibold text-[#111827] rounded-xl px-4 py-2.5 pr-10 outline-none focus:border-[#2e856e] transition-colors cursor-pointer"
                    >
                      {floorsList.length === 0 ? (
                        <option value="1">H-193, Sector-63</option>
                      ) : (
                        floorsList.map((f) => (
                          <option
                            key={f.id || f.floorId}
                            value={f.id || f.floorId}
                          >
                            {f.name ||
                              f.floorName ||
                              `Floor ${f.id || f.floorId}`}
                          </option>
                        ))
                      )}
                    </select>
                    <ChevronDown className="w-4 h-4 text-[#2e856e] absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                )}
              </div>

              {role === "teamLead" && (
                <div className="space-y-1.5 w-full max-w-sm">
                  <label className="block text-xs font-semibold text-[#4b5563]">
                    {loggedInRole === "teamLead" ? "Team" : "Select Team"}
                  </label>
                  {loggedInRole === "teamLead" ? (
                    <div className="flex items-center gap-2.5 bg-[#f0fdf4] border border-[#86efac] text-sm font-bold text-[#111827] rounded-xl px-4 py-2.5 shadow-2xs">
                      <div className="w-2.5 h-2.5 rounded-full bg-[#10b981] flex-shrink-0 animate-pulse" />
                      <span className="truncate">
                        {currentTLTeamName || "My Team"}
                      </span>
                    </div>
                  ) : teamsList.length > 0 ? (
                    <div className="relative">
                      <select
                        value={selectedTeamDropdown}
                        onChange={(e) =>
                          setSelectedTeamDropdown(e.target.value)
                        }
                        className="w-full appearance-none bg-[#f0fdf4] border border-[#86efac] text-sm font-semibold text-[#111827] rounded-xl px-4 py-2.5 pr-10 outline-none focus:border-[#2e856e] transition-colors cursor-pointer"
                      >
                        <option value="">Select Team</option>
                        {teamsList.map((t) => (
                          <option
                            key={t.id || t.teamId}
                            value={t.id || t.teamId}
                          >
                            {t.name || t.teamName || `Team ${t.id || t.teamId}`}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="w-4 h-4 text-[#2e856e] absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {/* Target Display Header */}
            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-700">
                  {role === "manager"
                    ? "Floor Target (Assigned by Admin)"
                    : "Team Target (Assigned by Manager)"}
                </span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                  Active Budget
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
              {/* Card 1: Target Value */}
              <div className="flex items-center gap-3.5 p-3.5 rounded-xl border border-gray-100 bg-gradient-to-br from-white to-gray-50/80 shadow-xs hover:border-emerald-200 transition-all">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0 border border-emerald-100/80 shadow-xs">
                  <TrendingUp className="w-5 h-5 stroke-[2.2]" />
                </div>
                <div>
                  <span className="text-[11px] font-semibold text-gray-500 block uppercase tracking-wider">
                    Target Value
                  </span>
                  <div className="text-base font-black text-gray-900 tracking-tight leading-tight mt-0.5">
                    {assignedTargetInfo.value
                      ? targetType === "revenue"
                        ? `₹${Number(assignedTargetInfo.value).toLocaleString("en-IN")}`
                        : targetType === "conversion"
                          ? `${assignedTargetInfo.value}%`
                          : `${Number(assignedTargetInfo.value).toLocaleString("en-IN")} Orders`
                      : "—"}
                  </div>
                </div>
              </div>

              {/* Card 2: Start Date */}
              <div className="flex items-center gap-3.5 p-3.5 rounded-xl border border-gray-100 bg-gradient-to-br from-white to-gray-50/80 shadow-xs hover:border-blue-200 transition-all">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0 border border-blue-100/80 shadow-xs">
                  <Calendar className="w-5 h-5 stroke-[2.2]" />
                </div>
                <div>
                  <span className="text-[11px] font-semibold text-gray-500 block uppercase tracking-wider">
                    Start Date
                  </span>
                  <div className="text-base font-bold text-gray-900 tracking-tight leading-tight mt-0.5">
                    {assignedTargetInfo.startDate || "—"}
                  </div>
                </div>
              </div>

              {/* Card 3: End Date */}
              <div className="flex items-center gap-3.5 p-3.5 rounded-xl border border-gray-100 bg-gradient-to-br from-white to-gray-50/80 shadow-xs hover:border-purple-200 transition-all">
                <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center flex-shrink-0 border border-purple-100/80 shadow-xs">
                  <Calendar className="w-5 h-5 stroke-[2.2]" />
                </div>
                <div>
                  <span className="text-[11px] font-semibold text-gray-500 block uppercase tracking-wider">
                    End Date
                  </span>
                  <div className="text-base font-bold text-gray-900 tracking-tight leading-tight mt-0.5">
                    {assignedTargetInfo.endDate || "—"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─────────────────── CARD 2: MIDDLE SELECTION CARD ─────────────────── */}
      <div className="w-full bg-white rounded-2xl border border-[#e5e7eb] shadow-sm p-6 md:p-8 space-y-4">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-[#111827]">
              {role === "admin"
                ? "Select Floors"
                : role === "manager"
                  ? "Select Team Leader"
                  : "Select Agent"}
            </h2>
            <span className="text-xs text-[#9ca3af] font-medium">
              {totalSelectedCount} of {totalItemsCount} selected
            </span>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            {/* Search Input Box */}
            <div className="relative min-w-[180px] sm:min-w-[220px]">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={
                  role === "admin"
                    ? "Search floors..."
                    : role === "manager"
                      ? "Search team leaders..."
                      : "Search agents..."
                }
                className="w-full pl-9 pr-7 py-1.5 text-xs bg-gray-50/90 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:border-[#2e856e] focus:ring-2 focus:ring-[#2e856e]/15 transition-all text-[#111827] placeholder-gray-400 font-medium"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 rounded-md cursor-pointer transition-colors"
                  title="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Select All Toggle */}
            <button
              type="button"
              onClick={() => {
                const targetList = searchQuery ? filteredAssignees : assignees;
                const allSelected =
                  targetList.length > 0 &&
                  targetList.every((a) => selectedIds.includes(a.id));
                if (allSelected) {
                  const targetIds = new Set(targetList.map((a) => a.id));
                  setSelectedIds((prev) =>
                    prev.filter((id) => !targetIds.has(id))
                  );
                } else {
                  const toAdd = targetList.map((a) => a.id);
                  setSelectedIds((prev) =>
                    Array.from(new Set([...prev, ...toAdd]))
                  );
                }
              }}
              className="px-3 py-1.5 rounded-xl border border-gray-200 text-xs font-bold text-gray-600 hover:bg-gray-50 transition-all cursor-pointer shadow-2xs"
            >
              {(searchQuery ? filteredAssignees : assignees).length > 0 &&
              (searchQuery ? filteredAssignees : assignees).every((a) =>
                selectedIds.includes(a.id)
              )
                ? "Deselect All"
                : "Select All"}
            </button>

            {/* ⚡ Auto Divide Button */}
            <button
              type="button"
              onClick={handleAutoDivide}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 text-[#065f46] border border-[#a7f3d0] text-xs font-black transition-all shadow-xs active:scale-95 cursor-pointer"
              title="Divide total entered target equally among selected members"
            >
              <Sparkles className="w-3.5 h-3.5 text-[#10b981]" />
              Auto Divide
            </button>
          </div>
        </div>

        {loadingAssignees ? (
          <div className="flex flex-col items-center justify-center py-12 text-[#6b7280]">
            <Loader2 className="w-7 h-7 animate-spin text-[#2e856e] mb-2" />
            <span className="text-xs font-semibold">Loading assignees...</span>
          </div>
        ) : assignees.length === 0 ? (
          <div className="py-14 text-center text-gray-400 text-sm font-medium">
            No{" "}
            {role === "admin"
              ? "floors"
              : role === "manager"
                ? "team leaders"
                : "agents"}{" "}
            found from backend.
          </div>
        ) : filteredAssignees.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm font-medium">
            <p>
              No{" "}
              {role === "admin"
                ? "floors"
                : role === "manager"
                  ? "team leaders"
                  : "agents"}{" "}
              matching &quot;
              <span className="font-bold text-gray-700">{searchQuery}</span>
              &quot;
            </p>
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="mt-2 text-xs text-[#2e856e] font-bold hover:underline cursor-pointer"
            >
              Clear Search
            </button>
          </div>
        ) : role === "admin" ? (
          /* ── ADMIN: 1-Column Floors List ── */
          <div className="space-y-3">
            {filteredAssignees.map((item) => {
              const isSelected = selectedIds.includes(item.id);
              const targetVal = item.current || "00";

              return (
                <div
                  key={item.id}
                  onClick={() => toggleSelect(item.id)}
                  className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer shadow-xs select-none ${
                    isSelected
                      ? "border-[#2e856e] bg-[#f0fdf4]/40"
                      : "border-[#f3f4f6] bg-white hover:border-[#e5e7eb]"
                  }`}
                >
                  <div className="flex items-center gap-3.5">
                    {/* Checkbox / Circle */}
                    {isSelected ? (
                      <div className="w-5 h-5 rounded-full bg-[#2e856e] flex items-center justify-center text-white flex-shrink-0 shadow-xs">
                        <Check className="w-3.5 h-3.5 stroke-[3]" />
                      </div>
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-gray-300 hover:border-[#2e856e] flex items-center justify-center flex-shrink-0 transition-colors bg-white" />
                    )}

                    <div>
                      <h4 className="text-sm font-bold text-[#111827]">
                        {item.name}
                      </h4>
                      {item.managers ? (
                        <p className="text-xs text-[#9ca3af] mt-0.5">
                          Managers: {item.managers}
                        </p>
                      ) : (
                        <p className="text-xs text-[#9ca3af] mt-0.5">
                          {item.role}
                        </p>
                      )}
                    </div>
                  </div>

                  <div
                    className="text-right flex-shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="text-[11px] text-[#9ca3af] block font-medium mb-1">
                      Current Target
                    </span>
                    <div className="border border-[#d1d5db] focus-within:border-[#2e856e] focus-within:ring-2 focus-within:ring-[#2e856e]/20 rounded-lg px-2.5 py-1 min-w-[64px] max-w-[85px] bg-white transition-all shadow-2xs inline-block">
                      <input
                        type="text"
                        value={
                          assigneeTargets[item.id] !== undefined
                            ? assigneeTargets[item.id]
                            : item.current || ""
                        }
                        onChange={(e) =>
                          handleAssigneeTargetChange(item.id, e.target.value)
                        }
                        placeholder="00"
                        className="w-full text-center text-sm font-bold text-[#111827] outline-hidden bg-transparent border-b border-gray-300 focus:border-[#2e856e] pb-0.5"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* ── MANAGER & TEAM LEADER: 2-Columns Grid Cards (Exact Same-to-Same with Editable Inputs) ── */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            {filteredAssignees.map((item) => {
              const isSelected = selectedIds.includes(item.id);

              return (
                <div
                  key={item.id}
                  onClick={() => toggleSelect(item.id)}
                  className={`flex items-center justify-between p-3.5 sm:p-4 rounded-xl border bg-white shadow-xs transition-all cursor-pointer select-none ${
                    isSelected
                      ? "border-[#10b981] bg-[#f0fdf4]/30"
                      : "border-[#e5e7eb] hover:border-[#cbd5e1]"
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {/* Circle Checkmark Icon */}
                    {isSelected ? (
                      <div className="w-5 h-5 rounded-full bg-[#10b981] flex items-center justify-center text-white flex-shrink-0 shadow-xs">
                        <Check className="w-3.5 h-3.5 stroke-[3]" />
                      </div>
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-gray-300 hover:border-[#10b981] flex items-center justify-center flex-shrink-0 transition-colors bg-white" />
                    )}

                    {/* Gradient Initials Avatar Circle */}
                    <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-[#4f46e5] to-[#818cf8] text-white flex items-center justify-center text-xs font-bold flex-shrink-0 shadow-xs">
                      {item.initials || "RV"}
                    </div>

                    <div className="min-w-0">
                      <h4 className="text-sm font-bold text-[#111827] truncate leading-tight">
                        {item.name}
                      </h4>
                      <p className="text-xs text-[#6b7280] mt-0.5">
                        {item.role ||
                          (role === "manager" ? "Team Lead" : "Agent")}
                      </p>
                    </div>
                  </div>

                  {/* Right Side: Current monthly label + Editable Input Box */}
                  <div
                    className="flex items-center gap-2.5 flex-shrink-0 pl-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="text-[11px] sm:text-xs text-[#6b7280] font-normal hidden sm:inline">
                      Current monthly
                    </span>
                    <div className="border border-[#d1d5db] focus-within:border-[#10b981] focus-within:ring-2 focus-within:ring-[#10b981]/20 rounded-lg px-2.5 py-1 min-w-[58px] max-w-[80px] text-center bg-white transition-all shadow-2xs">
                      <input
                        type="text"
                        value={
                          assigneeTargets[item.id] !== undefined
                            ? assigneeTargets[item.id]
                            : assigneeTargets[String(item.id)] !== undefined
                              ? assigneeTargets[String(item.id)]
                              : item.current || ""
                        }
                        onChange={(e) =>
                          handleAssigneeTargetChange(item.id, e.target.value)
                        }
                        placeholder="0"
                        className="w-full text-center text-sm font-bold text-[#111827] outline-hidden bg-transparent border-b border-gray-300 focus:border-[#10b981] pb-0.5"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ─────────────────── CARD 3: ASSIGNMENT SUMMARY CARD ─────────────────── */}
      <div className="w-full bg-white rounded-2xl border border-[#e5e7eb] shadow-sm p-5 md:p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-base font-bold text-[#111827]">
            Assignment Summary
          </h3>
          <div className="text-xs text-[#4b5563] mt-1 flex items-center gap-2 flex-wrap">
            <span>
              Target Type:{" "}
              <strong className="text-[#ea580c] font-semibold">
                {targetLabel}
              </strong>
            </span>
            <span className="text-gray-300">•</span>
            <span>
              Value:{" "}
              <strong className="text-[#111827] font-semibold">
                {targetDisplayVal}
              </strong>
            </span>
            <span className="text-gray-300">•</span>
            <span>
              Period:{" "}
              <strong className="text-[#111827] font-semibold">
                {defaultPeriodText}
              </strong>
            </span>
            <span className="text-gray-300">•</span>
            <span>
              Recipients:{" "}
              <strong className="text-[#111827] font-semibold">
                {recipientsLabel}
              </strong>
            </span>
          </div>
        </div>

        <button
          type="button"
          disabled={isSubmitting}
          onClick={handleAssignTarget}
          className={`flex items-center gap-1.5 px-6 py-3 rounded-xl text-sm font-bold shadow-sm transition-all active:scale-95 cursor-pointer ${
            totalSelectedCount > 0 && calculatedNumericTarget > 0
              ? "bg-[#2e856e] hover:bg-[#256c59] text-white shadow-md"
              : "bg-[#d1fae5] text-[#10b981] hover:bg-[#a7f3d0]"
          }`}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Assigning...
            </>
          ) : (
            <>
              <Plus className="w-4 h-4 stroke-[3]" />
              Assign Target
            </>
          )}
        </button>
      </div>

      {/* ─────────────────── SUCCESS TOAST POPUP ─────────────────── */}
      {toast && (
        <div className="fixed top-20 right-6 md:right-8 z-[99999] max-w-md w-[calc(100vw-3rem)] sm:w-[420px] transition-all duration-300 transform translate-y-0">
          <div className="bg-white text-[#111827] p-4 sm:p-5 rounded-2xl shadow-[0_20px_50px_rgba(16,185,129,0.18),0_10px_25px_rgba(0,0,0,0.08)] border-2 border-[#10b981] flex items-start gap-3.5 relative overflow-hidden">
            {/* Top Accent Gradient Bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#10b981] via-[#34d399] to-[#059669]" />

            {/* Checkmark Icon Container */}
            <div className="w-10 h-10 rounded-xl bg-[#ecfdf5] text-[#10b981] flex items-center justify-center flex-shrink-0 border border-[#a7f3d0] shadow-sm">
              <CheckCircle2 className="w-6 h-6 stroke-[2.5]" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 pr-1">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-bold text-[#065f46] tracking-tight">
                  {toast.title}
                </h4>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#d1fae5] text-[#047857] border border-[#a7f3d0]">
                  Success
                </span>
              </div>
              <p className="text-xs text-[#374151] mt-1.5 font-medium leading-relaxed">
                {toast.message}
              </p>
            </div>

            {/* Dismiss Close Button */}
            <button
              type="button"
              onClick={() => setToast(null)}
              className="text-[#9ca3af] hover:text-[#111827] p-1 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0 cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MISOrdersTarget;
